// Live order sync.
//
// The POS does not subscribe to the `orders` table. That table holds customer
// names, phone numbers and delivery addresses, and Realtime's postgres_changes
// stream enforces RLS — so subscribing from the browser would require an anon
// SELECT policy on orders, and the anon key ships inside this bundle. Instead
// the backend keeps a one-row `pos_sync_beacon` table whose timestamp a trigger
// bumps on every write to orders (migration 021 in the ordering repo). We listen
// for that timestamp changing and re-fetch through the authenticated /api/pos
// route, so no order data ever crosses the realtime channel.
//
// This is a fast path, never the only path: polling stays in place as the floor,
// because a websocket can be blocked by a captive portal or dropped by flaky
// restaurant wifi without warning.

const SUPABASE_URL = String(process.env.REACT_APP_SUPABASE_URL || '').trim();
const SUPABASE_ANON_KEY = String(process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();

const BEACON_TABLE = 'pos_sync_beacon';
const CHANNEL_NAME = 'pos-sync-beacon';
// A burst of writes (an amendment touching several rows, a daily inventory
// reset) should cause one refetch, not one per statement.
const COALESCE_MS = 400;

export const isRealtimeConfigured = () => Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

// Collapse rapid calls into a single trailing call. Exported for tests.
export function coalesce(fn, waitMs = COALESCE_MS) {
  let timer = null;
  const wrapped = (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, waitMs);
  };
  wrapped.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  return wrapped;
}

let cachedClient = null;

// supabase-js is ~57KB gzipped and nothing on the critical path needs it, so it
// is loaded as its own chunk once the POS is already running rather than being
// bundled into the initial download these tablets pay for on every deploy.
async function getClient() {
  if (cachedClient) return cachedClient;

  const { createClient } = await import('@supabase/supabase-js');
  if (!cachedClient) {
    cachedClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      // POS staff authenticate against the ordering backend, not Supabase Auth.
      // Without these the client would persist and try to refresh a session that
      // does not exist.
      auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
      realtime: { params: { eventsPerSecond: 5 } },
    });
  }
  return cachedClient;
}

// Subscribe to the beacon. `onChange` fires (coalesced) whenever an order is
// written anywhere — POS, customer site, rider app, or a manual database edit.
// `onStatus` receives the channel's lifecycle state so callers can tell whether
// the fast path is actually live. Returns an unsubscribe function synchronously
// (safe to use directly as a useEffect cleanup), and is a no-op when Realtime is
// not configured.
export function subscribeToOrderChanges(onChange, { onStatus } = {}) {
  const reportStatus = (status) => {
    if (typeof onStatus === 'function') onStatus(status);
  };

  if (!isRealtimeConfigured()) {
    reportStatus('unconfigured');
    return () => {};
  }

  const notify = coalesce(() => {
    if (typeof onChange === 'function') onChange();
  });

  let cancelled = false;
  let teardown = null;

  (async () => {
    const client = await getClient();
    // Unsubscribed while the chunk was still downloading.
    if (cancelled) return;

    const channel = client
      .channel(CHANNEL_NAME)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: BEACON_TABLE },
        () => notify(),
      )
      .subscribe(reportStatus);

    teardown = () => {
      try {
        client.removeChannel(channel);
      } catch {
        // channel already torn down
      }
    };
  })().catch((error) => {
    // A failed chunk load must not take the POS down — it just means this device
    // stays on polling.
    console.warn('[Realtime] could not start live order sync:', error);
    reportStatus('CHANNEL_ERROR');
  });

  return () => {
    cancelled = true;
    notify.cancel();
    if (teardown) teardown();
  };
}
